import { FC, useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  CircularProgress,
  Alert,
  Toolbar,
  Typography,
  IconButton,
  Tooltip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Chip,
  TableSortLabel,
  Snackbar,
  Menu,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoIcon from '@mui/icons-material/Info';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import HistoryIcon from '@mui/icons-material/History';
import LinkIcon from '@mui/icons-material/Link';
import { contentApi, FileItem, basePrefixApi } from '../services/api';
import { useAsync } from '../hooks/useAsync';
import { EditCSVModal } from './EditCSVModal';
import { UploadFileModal } from './UploadFileModal';
import { CategoriesEditor } from './CategoriesEditor';
import { CourseMappingEditor } from './CourseMappingEditor';
import { TabDescription } from './TabDescription';
import ImportFromEnvironmentDialog from './ImportFromEnvironmentDialog';
import { VersionHistoryDialog } from './VersionHistoryDialog';
import CourseLinkageDialog from './CourseLinkageDialog';

const FOLDER_DISPLAY_NAMES: Record<string, string> = {
  categories: 'Categories',
  courses: 'Courses',
  competency_frameworks: 'Competency Frameworks',
  learning_path_templates: 'Learning Path Templates',
  root: 'Configuration Files',
};

const TAB_DESCRIPTIONS: Record<string, { title: string; description: string; details: string[] }> = {
  categories: {
    title: 'Categories',
    description: 'Manage course categories for your Moodle instance. Categories organize courses hierarchically.',
    details: [
      'Create a categories.csv file with columns: category, parent, idnumber, description',
      'Use the "Edit Categories" button to create or edit categories in a structured form',
      'The parent field allows you to create hierarchical category structures',
      'Categories are used to organize and group courses in Moodle',
    ],
  },
  courses: {
    title: 'Courses',
    description: 'Manage Moodle course backup files (.mbz) and map them to categories.',
    details: [
      'Upload .mbz (Moodle backup) files to the courses folder',
      'Use "Map Courses to Categories" to create a course-mappings.csv file',
      'This file tells bootstrap_content.php which category to restore each course into',
      'Format: Each row maps a course backup file to a category path',
    ],
  },
  competency_frameworks: {
    title: 'Competency Frameworks',
    description: 'Store competency framework definitions for your Moodle instance.',
    details: [
      'Upload .csv or .xml files containing competency framework data',
      'These frameworks define the competencies and learning standards for your institution',
      'Use the file browser to upload, download, or delete framework files',
    ],
  },
  learning_path_templates: {
    title: 'Learning Path Templates',
    description: 'Store learning path configuration templates for your Moodle instance.',
    details: [
      'Upload .csv or .xml files containing learning path templates',
      'These templates define the structure and flow of learning paths',
      'Use the file browser to upload, download, or delete template files',
    ],
  },
  root: {
    title: 'Configuration Files',
    description: 'Manage root-level configuration files used by the bootstrap process.',
    details: [
      'course-mappings.csv: Maps course backup files to category paths for restoration',
      'This file is read by bootstrap_content.php to organize courses into categories',
      'Edit course mappings using the "Map Courses to Categories" button in the Courses tab',
      'You can download this file to review or backup your course mappings',
    ],
  },
};

interface ContentManagerProps {
  folder: string;
  currentEnvironment: string;
}

export const ContentManager: FC<ContentManagerProps> = ({ folder, currentEnvironment }) => {
  const [retryCount, setRetryCount] = useState(0);
  const [bucketName, setBucketName] = useState<string>('');
  const [bucketId, setBucketId] = useState<string>('');
  const [environmentId, setEnvironmentId] = useState<string>('');

  const { data: files, loading, error, execute: refreshFiles } = useAsync(
    () => contentApi.getFolderContentsWithVersions(folder),
    false, // Don't start immediately
  );

  // Fetch bucket name and IDs for current environment from backend
  // This ensures we use the exact environment selected in BasePrefixSelector
  useEffect(() => {
    const fetchEnvironmentInfo = async () => {
      try {
        // Get the currently selected environment from the backend
        // This avoids ambiguity when multiple environments share the same prefix
        const env = await basePrefixApi.getCurrentEnvironment();

        // Get full environment details to access bucket config
        const environments = await basePrefixApi.getEnvironmentsWithBuckets();
        const fullEnv = environments.find(e => e.environmentId === env.environmentId);

        if (fullEnv) {
          setBucketName(fullEnv.bucketConfig.bucket || '');
          setBucketId(fullEnv.bucketId);
          setEnvironmentId(fullEnv.environmentId);
        }
      } catch (err) {
        console.warn('Failed to fetch environment info:', err);
      }
    };

    if (currentEnvironment) {
      fetchEnvironmentInfo();
    }
  }, [currentEnvironment]);

  // Delayed initial load to ensure auth is ready
  useEffect(() => {
    const timer = setTimeout(() => {
      refreshFiles().catch((err) => {
        // If 401, retry once after a delay
        if (err?.response?.status === 401 && retryCount === 0) {
          console.warn('Auth error loading files, retrying...');
          setRetryCount(1);
          setTimeout(() => refreshFiles(), 500);
        }
      });
    }, 150);
    return () => clearTimeout(timer);
  }, [folder]);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [categoriesEditorOpen, setCategoriesEditorOpen] = useState(false);
  const [courseMappingEditorOpen, setCourseMappingEditorOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileItem | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [deleteConfirmFile, setDeleteConfirmFile] = useState<FileItem | null>(null);

  // Sorting state
  type SortField = 'name' | 'size' | 'lastModified' | string; // Allow metadata field names
  type SortOrder = 'asc' | 'desc';
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // Track last clicked item for shift-click range selection
  const [lastClickedIndex, setLastClickedIndex] = useState<number | null>(null);

  // Snackbar state for copy feedback
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [copiedText, setCopiedText] = useState('');

  // Metadata dialog
  const [metadataDialogOpen, setMetadataDialogOpen] = useState(false);
  const [selectedFileForMetadata, setSelectedFileForMetadata] = useState<FileItem | null>(null);

  // Visible metadata columns (only for courses folder)
  const [visibleMetadataColumns, setVisibleMetadataColumns] = useState<Set<string>>(new Set());
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);

  // Version history dialog
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [selectedFileForVersions, setSelectedFileForVersions] = useState<FileItem | null>(null);
  const [courseLinkageDialogOpen, setCourseLinkageDialogOpen] = useState(false);
  const [selectedCourseForLinkage, setSelectedCourseForLinkage] = useState<FileItem | null>(null);

  // Toggle metadata column visibility
  const toggleMetadataColumn = (columnKey: string) => {
    setVisibleMetadataColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        newSet.delete(columnKey);
      } else {
        newSet.add(columnKey);
      }
      return newSet;
    });
  };

  // Get available metadata fields from files
  const availableMetadataFields = (() => {
    if (folder !== 'courses' || !files) return [];

    const fieldsSet = new Set<string>();
    files.forEach(file => {
      if (file.metadata) {
        Object.keys(file.metadata).forEach(key => fieldsSet.add(key));
      }
    });
    return Array.from(fieldsSet).sort();
  })();

  // Get display name from metadata or filename
  const getDisplayName = (file: FileItem): string => {
    if (!file.metadata) return file.name;

    // For courses: prefer courseFullname, then courseShortname
    if (folder === 'courses') {
      const fullname = file.metadata['courseFullname'] || file.metadata['coursefullname'];
      if (fullname) return fullname;

      const shortname = file.metadata['courseShortname'] || file.metadata['courseshortname'];
      if (shortname) return shortname;
    }

    // For CMI5 activities: prefer cmi5-activity-name
    if (folder === 'cmi5_activities') {
      const activityName = file.metadata['cmi5-activity-name'];
      if (activityName) return activityName;
    }

    // For competency frameworks, learning paths, categories: use any available name field
    const nameField = file.metadata['name'] || file.metadata['title'] || file.metadata['displayname'];
    if (nameField) return nameField;

    // Fallback to filename
    return file.name;
  };

  // Format metadata field name for display
  const formatFieldName = (key: string): string => {
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey) {
        switch (event.key) {
          case 'a':
            if (!event.shiftKey) {
              event.preventDefault();
              handleSelectAll(true);
            }
            break;
          case 'd':
            if (event.shiftKey && selectedFiles.size > 0) {
              event.preventDefault();
              handleBulkDelete();
            }
            break;
        }
      } else if (event.key === 'Delete' && selectedFiles.size > 0) {
        event.preventDefault();
        handleBulkDelete();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedFiles.size]);

  const handleEditFile = (file: FileItem) => {
    // Only CSV files can be edited
    if (file.name.endsWith('.csv')) {
      // Use specialized editor for categories.csv
      if (folder === 'categories' && file.name === 'categories.csv') {
        setCategoriesEditorOpen(true);
      } else {
        setSelectedFile(file);
        setEditModalOpen(true);
      }
    }
  };

  const handleDeleteFile = async (file: FileItem) => {
    setDeleteConfirmFile(file);
  };

  const confirmDeleteFile = async () => {
    if (!deleteConfirmFile) return;
    
    setDeleteLoading(deleteConfirmFile.key);
    try {
      await contentApi.deleteFile(folder, deleteConfirmFile.name);
      await refreshFiles();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleteLoading(null);
      setDeleteConfirmFile(null);
    }
  };

  const handleSelectFile = (fileKey: string, selected: boolean, index: number, shiftKey: boolean = false) => {
    const newSelected = new Set(selectedFiles);

    // Handle shift-click range selection
    if (shiftKey && lastClickedIndex !== null && sortedFiles.length > 0) {
      const start = Math.min(lastClickedIndex, index);
      const end = Math.max(lastClickedIndex, index);

      // Select all items in the range
      for (let i = start; i <= end; i++) {
        if (sortedFiles[i]) {
          newSelected.add(sortedFiles[i].key);
        }
      }
      setLastClickedIndex(index);
    } else {
      // Normal single selection
      if (selected) {
        newSelected.add(fileKey);
      } else {
        newSelected.delete(fileKey);
      }
      setLastClickedIndex(index);
    }

    setSelectedFiles(newSelected);
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      const allFileKeys = files?.map(file => file.key) || [];
      setSelectedFiles(new Set(allFileKeys));
    } else {
      setSelectedFiles(new Set());
    }
  };

  const handleBulkDelete = () => {
    if (selectedFiles.size === 0) return;
    setBulkDeleteModalOpen(true);
  };

  const confirmBulkDelete = async () => {
    if (selectedFiles.size === 0) return;

    setBulkDeleteLoading(true);
    try {
      const selectedFilesList = files?.filter(file => selectedFiles.has(file.key)) || [];
      const filenames = selectedFilesList.map(file => file.name);
      
      // Use bulk delete API for efficiency
      await contentApi.deleteFiles(folder, filenames);
      
      // Clear selection and refresh
      setSelectedFiles(new Set());
      await refreshFiles();
      setBulkDeleteModalOpen(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Bulk delete failed');
    } finally {
      setBulkDeleteLoading(false);
    }
  };

    const handleDownloadFile = async (file: FileItem) => {
    try {
      // Use the binary download endpoint for proper file handling
      const blob = await contentApi.downloadFile(folder, file.name);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download file:', error);
      alert('Failed to download file');
    }
  };

  const handleImportComplete = () => {
    refreshFiles(); // Refresh the file list after importing
    setSelectedFiles(new Set()); // Clear any selections
  };

  const handleCopyS3Url = async (file: FileItem) => {
    const s3Url = bucketName ? `s3://${bucketName}/${file.key}` : file.key;
    try {
      await navigator.clipboard.writeText(s3Url);
      setCopiedText(s3Url);
      setCopySnackbarOpen(true);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      alert('Failed to copy to clipboard');
    }
  };

  // Sorting logic
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with ascending order
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Sort files based on current sort field and order
  const sortedFiles = files ? [...files].sort((a, b) => {
    let comparison = 0;

    switch (sortField) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'size':
        comparison = a.size - b.size;
        break;
      case 'lastModified':
        comparison = new Date(a.lastModified).getTime() - new Date(b.lastModified).getTime();
        break;
      default:
        // Handle metadata field sorting
        const aValue = a.metadata?.[sortField] || '';
        const bValue = b.metadata?.[sortField] || '';
        comparison = aValue.localeCompare(bValue);
        break;
    }

    return sortOrder === 'asc' ? comparison : -comparison;
  }) : [];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {TAB_DESCRIPTIONS[folder] && (
        <TabDescription
          title={TAB_DESCRIPTIONS[folder].title}
          description={TAB_DESCRIPTIONS[folder].description}
          details={TAB_DESCRIPTIONS[folder].details}
        />
      )}

      <Paper>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {FOLDER_DISPLAY_NAMES[folder] || folder}
            {selectedFiles.size > 0 && (
              <Chip 
                label={`${selectedFiles.size} selected`} 
                size="small" 
                sx={{ ml: 2 }}
              />
            )}
          </Typography>
          
          {selectedFiles.size > 0 && (
            <Tooltip title="Delete selected files (Ctrl+Shift+D or Delete key)">
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteSweepIcon />}
                onClick={handleBulkDelete}
                sx={{ mr: 1 }}
                disabled={bulkDeleteLoading}
              >
                Delete Selected ({selectedFiles.size})
              </Button>
            </Tooltip>
          )}
          
          {folder === 'categories' && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={() => setCategoriesEditorOpen(true)}
              sx={{ mr: 1 }}
            >
              Edit Categories
            </Button>
          )}
          {folder === 'courses' && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={() => setCourseMappingEditorOpen(true)}
              sx={{ mr: 1 }}
            >
              Map Courses to Categories
            </Button>
          )}
          <Tooltip title="Refresh file list">
            <IconButton
              onClick={() => refreshFiles()}
              disabled={loading}
              sx={{ mr: 1 }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {folder === 'courses' && availableMetadataFields.length > 0 && (
            <>
              <Tooltip title="Toggle metadata columns">
                <IconButton
                  onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
                  sx={{ mr: 1 }}
                >
                  <ViewColumnIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={columnMenuAnchor}
                open={Boolean(columnMenuAnchor)}
                onClose={() => setColumnMenuAnchor(null)}
              >
                <MenuItem disabled sx={{ opacity: 1, fontWeight: 'bold' }}>
                  Show Columns
                </MenuItem>
                {availableMetadataFields.map(field => (
                  <MenuItem key={field} onClick={() => toggleMetadataColumn(field)}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={visibleMetadataColumns.has(field)}
                          size="small"
                        />
                      }
                      label={formatFieldName(field)}
                      sx={{ width: '100%', mr: 0 }}
                    />
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
          <Button
            variant="outlined"
            startIcon={<CloudDownloadIcon />}
            onClick={() => setImportModalOpen(true)}
            sx={{ mr: 1 }}
          >
            Import
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setUploadModalOpen(true)}
          >
            Upload
          </Button>
        </Toolbar>

        {error && <Alert severity="error">{error.message}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell padding="checkbox">
                    <Tooltip title="Select all files (Ctrl+A)">
                      <Checkbox
                        indeterminate={selectedFiles.size > 0 && selectedFiles.size < (files?.length || 0)}
                        checked={files?.length === 0 ? false : selectedFiles.size === files?.length}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        disabled={!files || files.length === 0}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'name'}
                      direction={sortField === 'name' ? sortOrder : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortField === 'size'}
                      direction={sortField === 'size' ? sortOrder : 'asc'}
                      onClick={() => handleSort('size')}
                    >
                      Size
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortField === 'lastModified'}
                      direction={sortField === 'lastModified' ? sortOrder : 'asc'}
                      onClick={() => handleSort('lastModified')}
                    >
                      Last Modified
                    </TableSortLabel>
                  </TableCell>
                  {/* Metadata columns */}
                  {folder === 'courses' && availableMetadataFields.map(field =>
                    visibleMetadataColumns.has(field) && (
                      <TableCell key={field} align="left">
                        <TableSortLabel
                          active={sortField === field}
                          direction={sortField === field ? sortOrder : 'asc'}
                          onClick={() => handleSort(field)}
                        >
                          {formatFieldName(field)}
                        </TableSortLabel>
                      </TableCell>
                    )
                  )}
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedFiles && sortedFiles.length > 0 ? (
                  sortedFiles.map((file, index) => (
                    <TableRow
                      key={file.key}
                      hover
                      selected={selectedFiles.has(file.key)}
                      sx={{
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        },
                        '&.Mui-selected:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.12)',
                        }
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedFiles.has(file.key)}
                          onChange={(e) => {
                            const shiftKey = (e.nativeEvent as MouseEvent).shiftKey;
                            handleSelectFile(file.key, e.target.checked, index, shiftKey);
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip
                            title={`${getDisplayName(file)}${file.name !== getDisplayName(file) ? `\nFile: ${file.name}` : ''}\n${bucketName ? `s3://${bucketName}/${file.key}` : file.key}`}
                            placement="top"
                          >
                            <span>{getDisplayName(file)}</span>
                          </Tooltip>
                          {file.versionInfo && file.versionInfo.hasVersionHistory && (
                            <Chip
                              label={`${file.versionInfo.currentVersion} (${file.versionInfo.totalVersions})`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                          {file.versionInfo && file.versionInfo.currentVersion === 'legacy' && (
                            <Chip
                              label="Legacy"
                              size="small"
                              color="default"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                          <Tooltip title="Copy S3 URL to clipboard">
                            <IconButton
                              size="small"
                              onClick={() => handleCopyS3Url(file)}
                              sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                            >
                              <ContentCopyIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          {file.metadata && Object.keys(file.metadata).length > 0 && (
                            <Tooltip title="View metadata">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedFileForMetadata(file);
                                  setMetadataDialogOpen(true);
                                }}
                                sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                              >
                                <InfoIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{formatBytes(file.size)}</TableCell>
                      <TableCell align="right">{formatDateTime(file.lastModified)}</TableCell>
                      {/* Metadata cells */}
                      {folder === 'courses' && availableMetadataFields.map(field =>
                        visibleMetadataColumns.has(field) && (
                          <TableCell key={field} align="left">
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                              {file.metadata?.[field] || '-'}
                            </Typography>
                          </TableCell>
                        )
                      )}
                      <TableCell align="right">
                        {file.versionInfo && file.versionInfo.currentVersion !== 'legacy' && (
                          <Tooltip title="Version History">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedFileForVersions(file);
                                setVersionHistoryOpen(true);
                              }}
                            >
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {folder === 'courses' && file.name.endsWith('.mbz') && (
                          <Tooltip title="Manage Linkages">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedCourseForLinkage(file);
                                setCourseLinkageDialogOpen(true);
                              }}
                            >
                              <LinkIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {file.name.endsWith('.csv') && (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEditFile(file)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadFile(file)}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteFile(file)}
                            disabled={deleteLoading === file.key}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5 + visibleMetadataColumns.size} align="center" sx={{ py: 4 }}>
                      No files in this folder
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {selectedFile && (
        <EditCSVModal
          open={editModalOpen}
          folder={folder}
          filename={selectedFile.name}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedFile(null);
          }}
          onSave={() => refreshFiles()}
        />
      )}

      {folder === 'categories' && (
        <CategoriesEditor
          open={categoriesEditorOpen}
          onClose={() => setCategoriesEditorOpen(false)}
          onSaveSuccess={() => {
            setCategoriesEditorOpen(false);
            refreshFiles();
          }}
        />
      )}

      {folder === 'courses' && (
        <CourseMappingEditor
          open={courseMappingEditorOpen}
          onClose={() => setCourseMappingEditorOpen(false)}
          onSaveSuccess={() => {
            setCourseMappingEditorOpen(false);
            refreshFiles();
          }}
        />
      )}

      <UploadFileModal
        open={uploadModalOpen}
        folder={folder}
        onClose={() => setUploadModalOpen(false)}
        onUploadSuccess={() => refreshFiles()}
      />

      <ImportFromEnvironmentDialog
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        currentEnvironment={currentEnvironment}
        targetFolder={folder}
        onImportComplete={handleImportComplete}
      />

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteModalOpen} onClose={() => setBulkDeleteModalOpen(false)}>
        <DialogTitle>Delete {selectedFiles.size} Files?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete the following {selectedFiles.size} files? This action cannot be undone.
          </Typography>
          <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
            {files?.filter(file => selectedFiles.has(file.key)).map((file) => (
              <ListItem key={file.key}>
                <ListItemText
                  primary={file.name}
                  secondary={`${formatBytes(file.size)} • ${formatDate(file.lastModified)}`}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteModalOpen(false)} disabled={bulkDeleteLoading}>
            Cancel
          </Button>
          <Button 
            onClick={confirmBulkDelete} 
            color="error" 
            variant="contained"
            disabled={bulkDeleteLoading}
            startIcon={bulkDeleteLoading ? <CircularProgress size={16} /> : <DeleteSweepIcon />}
          >
            {bulkDeleteLoading ? 'Deleting...' : `Delete ${selectedFiles.size} Files`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Single File Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirmFile}
        onClose={() => setDeleteConfirmFile(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete File?</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete this file?
          </Typography>
          {deleteConfirmFile && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight="bold">
                {deleteConfirmFile.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatBytes(deleteConfirmFile.size)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmFile(null)}>
            Cancel
          </Button>
          <Button onClick={confirmDeleteFile} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Metadata Dialog */}
      <Dialog
        open={metadataDialogOpen}
        onClose={() => {
          setMetadataDialogOpen(false);
          setSelectedFileForMetadata(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          File Metadata
          {selectedFileForMetadata && (
            <Typography variant="caption" display="block" color="text.secondary">
              {selectedFileForMetadata.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedFileForMetadata?.metadata ? (
            <List dense>
              {Object.entries(selectedFileForMetadata.metadata).map(([key, value]) => (
                <ListItem key={key} sx={{ px: 0 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 150 }}>
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {value}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography color="text.secondary">No metadata available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setMetadataDialogOpen(false);
              setSelectedFileForMetadata(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Confirmation Snackbar */}
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setCopySnackbarOpen(false)}
        message="S3 URL copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Version History Dialog */}
      {selectedFileForVersions && (
        <VersionHistoryDialog
          open={versionHistoryOpen}
          onClose={() => {
            setVersionHistoryOpen(false);
            setSelectedFileForVersions(null);
          }}
          folder={folder}
          filename={selectedFileForVersions.name}
          onVersionRestored={() => {
            refreshFiles();
          }}
        />
      )}

      {selectedCourseForLinkage && bucketId && environmentId && (
        <CourseLinkageDialog
          open={courseLinkageDialogOpen}
          onClose={() => {
            setCourseLinkageDialogOpen(false);
            setSelectedCourseForLinkage(null);
          }}
          bucketId={bucketId}
          environmentId={environmentId}
          courseFilename={selectedCourseForLinkage.name}
          courseName={selectedCourseForLinkage.name.replace('.mbz', '')}
        />
      )}
    </Container>
  );
};

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString();
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
}
